import type { MapEngine } from "./map-engine.js";
import type {
  PackManifest,
  PackDef,
  PackLayerData,
  PackInfo,
} from "./layer-packs.js";

/**
 * Fetches a layer pack manifest and materializes packs into the MapEngine
 * using its existing layer/marker API.
 *
 * Pack markers get IDs of the form "{layerId}:{explicitId || index}" to avoid
 * collisions with SDK-created markers.
 */
export class LayerPackLoader {
  private engine: MapEngine;
  private baseUrl: string;
  private manifest: PackManifest | null = null;
  private loadedPacks = new Set<string>();
  private loadingPacks = new Map<string, Promise<void>>();

  /**
   * @param engine  The MapEngine instance to create layers/markers on.
   * @param baseUrl Base URL for the layers directory (e.g. "" for same-origin,
   *                or "https://cdn.example.com"). The manifest is fetched at
   *                `${baseUrl}/layers/manifest.json`.
   */
  constructor(engine: MapEngine, baseUrl = "") {
    this.engine = engine;
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  /** Suffix to append to layer data/image URLs for cache-busting (e.g. "?v=abc"). */
  private versionSuffix(): string {
    const v = this.manifest?.version;
    return v ? `?v=${encodeURIComponent(v)}` : "";
  }

  /** Fetch and cache the manifest. No-ops if already loaded. */
  async loadManifest(): Promise<PackManifest> {
    if (this.manifest) return this.manifest;
    const url = `${this.baseUrl}/layers/manifest.json`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch pack manifest: ${res.status}`);
    this.manifest = (await res.json()) as PackManifest;
    return this.manifest;
  }

  /** Load all packs in the manifest. */
  async loadAllPacks(): Promise<void> {
    const manifest = await this.loadManifest();
    await Promise.all(manifest.packs.map((p) => this.loadPack(p.id)));
  }

  /** Load specific packs by ID. Unknown IDs are silently skipped. */
  async loadPacks(packIds: string[]): Promise<void> {
    await this.loadManifest();
    await Promise.all(packIds.map((id) => this.loadPack(id)));
  }

  /** Load a single pack. No-ops if already loaded. */
  async loadPack(packId: string): Promise<void> {
    if (this.loadedPacks.has(packId)) return;

    // Deduplicate concurrent loads of the same pack
    const existing = this.loadingPacks.get(packId);
    if (existing) return existing;

    const promise = this._doLoadPack(packId);
    this.loadingPacks.set(packId, promise);
    try {
      await promise;
    } finally {
      this.loadingPacks.delete(packId);
    }
  }

  private async _doLoadPack(packId: string): Promise<void> {
    if (!this.manifest) await this.loadManifest();
    const packDef = this.manifest!.packs.find((p) => p.id === packId);
    if (!packDef) {
      console.warn(`[LayerPackLoader] Pack "${packId}" not found in manifest`);
      return;
    }

    // Separate image layers from marker layers
    const markerLayers = packDef.layers.filter((l) => (l.type ?? "markers") === "markers");
    const imageLayers = packDef.layers.filter((l) => l.type === "image");

    // Fetch all marker layer data files in parallel
    const versionSuffix = this.versionSuffix();
    const layerDataEntries = await Promise.all(
      markerLayers.map(async (layerDef) => {
        const url = `${this.baseUrl}/layers/${layerDef.dataFile}${versionSuffix}`;
        try {
          const res = await fetch(url);
          if (!res.ok) {
            console.warn(`[LayerPackLoader] Failed to fetch ${url}: ${res.status}`);
            return { layerDef, data: null };
          }
          const data = (await res.json()) as PackLayerData;
          return { layerDef, data };
        } catch (e) {
          console.warn(`[LayerPackLoader] Error fetching ${url}:`, e);
          return { layerDef, data: null };
        }
      }),
    );

    // Materialize marker layers
    for (let i = 0; i < layerDataEntries.length; i++) {
      const { layerDef, data } = layerDataEntries[i];
      const category = layerDef.category ?? packDef.label;

      this.engine.createLayer(layerDef.id, {
        label: layerDef.label,
        visible: layerDef.visible,
        opacity: layerDef.opacity,
        minZoom: layerDef.minZoom,
        category,
        hidden: layerDef.hidden,
      });

      if (!data) continue;

      const style = layerDef.style ?? {};

      if (data.markers) {
        for (let mi = 0; mi < data.markers.length; mi++) {
          const m = data.markers[mi];
          const markerId = m.id ?? `${layerDef.id}:${mi}`;

          this.engine.addMarker({
            id: markerId,
            layer: layerDef.id,
            x: m.x,
            y: m.y,
            icon: m.icon ?? style.icon,
            shape: m.shape ?? style.shape,
            text: m.text ?? style.text,
            tooltip: m.tooltip,
            richTooltip: m.richTooltip,
            color: m.color ?? style.color,
            size: m.size ?? style.size,
            data: m.data,
          });
        }
      }
    }

    // Materialize image overlay layers (loaded async, non-blocking)
    for (const layerDef of imageLayers) {
      const category = layerDef.category || undefined;

      this.engine.createLayer(layerDef.id, {
        label: layerDef.label,
        visible: layerDef.visible,
        opacity: layerDef.opacity,
        minZoom: layerDef.minZoom,
        category,
        hidden: layerDef.hidden,
      });

      if (layerDef.imageUrl) {
        const imageUrl = `${this.baseUrl}/layers/${layerDef.imageUrl}${versionSuffix}`;
        this.engine.addImageOverlay(layerDef.id, imageUrl, {
          imageGrid: layerDef.imageGrid,
        });
      }
    }

    this.loadedPacks.add(packId);
  }

  /** Return info about all packs in the manifest (loads manifest if needed). */
  async getAvailablePacks(): Promise<PackInfo[]> {
    const manifest = await this.loadManifest();
    return manifest.packs.map((p) => this.packDefToInfo(p));
  }

  /** Return info synchronously if manifest is already loaded. */
  getAvailablePacksSync(): PackInfo[] {
    if (!this.manifest) return [];
    return this.manifest.packs.map((p) => this.packDefToInfo(p));
  }

  private packDefToInfo(p: PackDef): PackInfo {
    return {
      id: p.id,
      label: p.label,
      description: p.description,
      layers: p.layers.map((l) => ({ id: l.id, label: l.label })),
      loaded: this.loadedPacks.has(p.id),
    };
  }
}
